/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpStatusCode} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {FilterTvpScenarioRequestModel} from '../models/filter-tvp-scenario-request.model';
import {PivotTableVehDataRequestDTO} from '../models/pivot-table-veh-data-request-dto.model';
import {MarketShareSegmentReportRequest} from '../models/reports/market-share-segment-report-request.model';
import {TvpVehicleProgram} from '../models/tvp-vehicle-program.model';
import {UpdateForecastWithScenarioRequest} from '../models/update-veh-forecast-with-scenario-request.model';
import {VehicleQueryRequestDTO} from "../models/vehicle-query-request-dto.model";
import {VehicleScenarioCreateDtoModel} from '../models/vehicle-scenario-create-dto.model';
import {VehicleScenario} from '../models/vehicle-scenario-dto.model';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})

export class VehicleScenarioService {
  public vehicleScenario: VehicleScenario;

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * Sends a get request to retrieve list of all fcorgs the user has access to
   */
  getFcorgsByGmid(gmid: string): Observable<string[]> {
    const params = new HttpParams().set('gmid', gmid);
    return this.http.get<any>(`${this.configService.baseApiUrl}fcorgs/by-id`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of all vehicle scenario categories in the database.
   */
  getVehicleScenarios(gmid: string): Observable<any> {
    const params = new HttpParams().set('gmid', gmid);
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of vehicle scenarios by fcorg in the database.
   */
  getVehicleScenariosByFcorg(gmid: string, fcorg: string): Observable<any> {
    const params = new HttpParams().set('gmid', gmid).set('fcorg', fcorg);
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Send a get request to retrieve a list of vehicle scenarios by industry scenario id
   * @param industryScenarioId industry scenario id
   */
  getVehicleScenarioByIndustryScenarioId(industryScenarioId: number): Observable<any> {
    const params = new HttpParams().set('industryScenarioId', industryScenarioId);
    return this.http.get<any[]>(`${this.configService.baseApiUrl}vehicle-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Send a get request to retrieve list of vehicle scenarios that contain the propulsion scenario id
   * @param propulsionScenarioId propulsion scenario id
   */
  getVehicleScenarioByPropScenId(propulsionScenarioId: number): Observable<any> {
    const params = new HttpParams().set('propulsionScenarioId', propulsionScenarioId);
    return this.http.get<any[]>(`${this.configService.baseApiUrl}vehicle-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of all vehicle scenarios with associations
   */
  getAllVehicleScenariosWithAssociations(): any {
    return this.http.get<any[]>(`${this.configService.baseApiUrl}vehicle-scenario/associations`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of all scenario categories in the database.
   */
  getAllScenarioCategories(): Observable<any> {
    return this.http.get<any>(`${this.configService.baseApiUrl}manage-scenario-category-list`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of all scenario types in the database.
   */
  getAllScenarioTypes(): any {
    return this.http.get<any[]>(`${this.configService.baseApiUrl}manage-scenario-type-list`)
    .pipe(catchError(myError => this.handleError(myError)));

  }

  /**
   * Sends a get request to retrieve list of all propulsion types in the database.
   */
  getAllPropulsionTypes(): any {
    return this.http.get<any[]>(`${this.configService.baseApiUrl}manage-propulsion-types`)
    .pipe(catchError(myError => this.handleError(myError)));

  }

  /**
   * Sends a get request to retrieve list of all forecast states in the database.
   */
  getAllForecastStates(): any {
    return this.http.get<any[]>(`${this.configService.baseApiUrl}manage-forecast-state`)
    .pipe(catchError(myError => this.handleError(myError)));

  }

  /**
   * Sends a get request to retrieve list of all engineering program classes in the database.
   */
  getAllEngProgClass(): any {
    return this.http.get<any[]>(`${this.configService.baseApiUrl}engineering-program-class`)
    .pipe(catchError(myError => this.handleError(myError)));

  }

  /**
   * Sends a get request to retrieve list of industry scenarios by fcorg
   */
  getIndustryScenarios(fcorg: string, hidden = false): Observable<any> {
    const params = new HttpParams().set('fcorg', fcorg).set('hidden', hidden);
    return this.http.get<any>(`${this.configService.baseApiUrl}industry-scenarios`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * send a put request to update the vehicle scenario with new data
   * @param vehicleScenario vehicle scenario dto
   */
  updateVehicleScenario(vehicleScenario: VehicleScenario): Observable<VehicleScenario> {
    return this.http.put<VehicleScenario>(`${this.configService.baseApiUrl}vehicle-scenario`, vehicleScenario);
  }

  /**
   * Send PUT request to update a vehicle scenario with the vehicle portfolio
   * @param vehScenId the vehicle scenario id
   * @param gmid the id of the user
   * @param arch the architecture value (only applicable to TVP scenarios)
   * @param mktDiv concatenated string of market divisions (only applicable to TVP scenarios)
   * @param brand concatenated string of brands (only applicable to TVP scenarios)
   * @param prgmCode concatenated string of program codes (only applicable to TVP scenarios)
   * @param styleCode concatenated string of style codes (only applicable to TVP scenarios)
   * @param bodyStyle concatenated string of body style (only applicable to TVP scenarios)
   */
  updateVehicleScenarioWithPortfolio(vehScenId: number, gmid: string, arch: string, mktDiv: string,
                                     brand: string, prgmCode: string, styleCode: string, bodyStyle: string): Observable<any> {
    const requestBody = {vehScenId, gmid, arch, mktDiv, brand, prgmCode, styleCode, bodyStyle};
    return this.http.put<any>(`${this.configService.baseApiUrl}vehicle-scenario/with-portfolio`, requestBody)
    .pipe(catchError(myError => this.handleError(myError)));

  }

  /**
   * send a delete request to delete vehicle scenario by id
   * @param vehicleScenarioId vehicle scenario id
   */
  deleteVehicleScenario(vehicleScenarioId: number): Observable<any> {
    const params = new HttpParams().set('vehicleScenarioId', vehicleScenarioId);
    return this.http.delete(`${this.configService.baseApiUrl}vehicle-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get list of vehicle scenarios that show up in the Transaction Log as having a duplicate.
   */
  getVehScenListForDupeCheck(): Observable<VehicleScenario[]> {
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario/duplicates`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Scan all Vehicle Scenarios for any duplicate records
   * @param gmid GMID of the user
   */
  scanForVehScenDupes(gmid: string): Observable<void> {
    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/duplicates/scan`, {}, {
      params: new HttpParams().set('gmid', gmid)
    }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Runs a clean up on removing duplicate records from the selected vehicle scenario
   * @param gmid GMID of the user
   * @param vehScenId vehicle scenario id
   */
  removeVehScenDupes(gmid: string, vehScenId: string): Observable<void> {
    const formData: FormData = new FormData();
    formData.append('gmid', gmid);
    formData.append('vehicleScenarioId', vehScenId);
    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/duplicates/remove`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }

  getGlobalVehicleScenarioMapByVehicleScenarioId(vehicleScenarioId: number): Observable<any> {
    const params = new HttpParams().set('vehicleScenarioId', vehicleScenarioId);
    return this.http.get<any[]>(`${this.configService.baseApiUrl}vehicle-scenario/global-scenario`,
      {params}).pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to retrieve list of Vehicle Scenarios
   * @param gmid User's GMID
   * @param hidden Show hidden flag
   */
  getVehicleScenariosForSelect(gmid: string, hidden: boolean): Observable<any> {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('hidden', hidden);
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets the fcorgs used in industry scenarios for create from portfolio
   * @param gmid user's GMID
   */
  getFcorgsForPortfolio(gmid: string): Observable<any> {
    const params = new HttpParams()
    .set('gmid', gmid);
    return this.http.get<any>(`${this.configService.baseApiUrl}fcorgs/port-list`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets the architectures for create from portfolio TVP
   */
  getTvpArchitectures(): Observable<any> {
    return this.http.get<any>(`${this.configService.baseApiUrl}portfolio/vehicle/tvp-arch`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets the vehicle programs to select when creating TVP scenario from portfolio
   * @param arch the architecture to filter the results by
   */
  getTvpVehiclePrograms(arch: string): Observable<any> {
    const params = new HttpParams()
    .set('arch', arch);
    return this.http.get<any>(`${this.configService.baseApiUrl}portfolio/vehicle/tvp-programs`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }


  /**
   * Sends a get request to retrieve list of Vehicle Scenarios
   * @param gmid User's GMID
   * @param hidden Show hidden flag
   * @param tvp indicate whether to get tvp scenarios or not
   */
  getVehicleScenariosForCreate(gmid: string, hidden: boolean, tvp: boolean): Observable<any> {

    const params = new HttpParams()
    .set('gmid', gmid)
    .set('hidden', hidden)
    .set('tvp', tvp);
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends the file to the backend to process it and extract the fcorg
   * @param file the uploaded Excel file
   * @param sheetName the name of the sheet to use
   */
  getFcorgsFromTemplate(file: File, sheetName: string): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheetName', sheetName);
    return this.http.post<any>(`${this.configService.baseApiUrl}fcorgs`, formData).pipe(catchError(myError => this.handleError(myError)));
  }


  /**
   * Gets a vehicle scenario by name if the user has access to it
   * @param gmid User's GMID
   * @param name the name of the vehicle scenario
   */
  getVehicleScenarioByName(gmid: string, name: string): Observable<any> {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('name', name);
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }


  /**
   * Sends a post request to the endpoint to create a scenario from a scenario
   * @param name the scenario name
   * @param scenCat the scenario category
   * @param scenType the scenario type
   * @param assm the scenario assumption
   * @param indScenId the id of the related scenario
   * @param gmid the gmid of the user
   * @param fcorg the fcorg of the base scenario
   * @param keepSharedVol 1 if keeping shared volume, 0 if keeping GM volume the same
   * @param scenId The id of the base vehicle scenario
   */
  createVehicleScenarioFromScenario(name: string, scenCat: string, scenType: string, assm: string, indScenId: number,
                                    gmid: string, fcorg: string, keepSharedVol: number, scenId: number): Observable<void> {
    const dto: VehicleScenarioCreateDtoModel = {
      name,
      scenCat,
      scenType,
      assm,
      scenId,
      indScenId,
      keepSharedVol,
      propTypeId: null,
      engClassId: null,
      fcStateId: null,
      gmid,
      fcorg,
      programList: null,
    };
    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/from-scenario`, dto)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a post request to the endpoint to create a scenario from portfolio
   * @param name the scenario name
   * @param scenCat the scenario category
   * @param scenType the scenario type
   * @param assm the scenario assumption
   * @param indScenId the id of the related industry scenario
   * @param gmid the gmid of the user
   * @param fcorg the chosen fcorg
   */
  createVehicleScenarioFromPortfolio(name: string, scenCat: string, scenType: string, assm: string, indScenId: number,
                                     gmid: string, fcorg: string): Observable<void> {

    const dto: VehicleScenarioCreateDtoModel = {
      name,
      scenCat,
      scenType,
      assm,
      scenId: null,
      indScenId,
      keepSharedVol: null,
      propTypeId: null,
      engClassId: null,
      fcStateId: null,
      gmid,
      fcorg,
      programList: null,
    };
    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/from-portfolio`, dto)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a post request to the endpoint to create a scenario from portfolio
   * @param name the scenario name
   * @param scenCat the scenario category
   * @param scenType the scenario type
   * @param assm the scenario assumption
   * @param indScenId the id of the related industry scenario
   * @param gmid the gmid of the user
   * @param sheetName the name of the sheet to use
   * @param file the uploaded excel file
   */
  createVehicleScenarioFromTemplate(name: string, scenCat: string, scenType: string, assm: string, indScenId: number,
                                    gmid: string, sheetName: string, file: File): Observable<void> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-name', sheetName);
    formData.append('gmid', gmid);
    formData.append('scen-cat', scenCat);
    formData.append('scen-type', scenType);
    formData.append('name', name);
    formData.append('assm', assm);
    formData.append('ind-scen-id', indScenId.toString());

    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/from-template`, formData)
    .pipe(catchError(myError => this.handleError(myError)));
  }


  /**
   * Sends a post request to the endpoint to create a TVP scenario from a scenario
   * @param name the scenario name
   * @param scenCat the scenario category
   * @param scenType the scenario type
   * @param assm the scenario assumption
   * @param indScenId the id of the related scenario
   * @param gmid the gmid of the user
   * @param fcorg the fcorg of the base scenario
   * @param propTypeId the propulsion type id
   * @param engClassId the engineering program class
   * @param fcstateId the forecast state id
   * @param scenId The id of the base vehicle scenario
   */
  createVehicleScenarioFromScenarioTVP(name: string, scenCat: string, scenType: string, assm: string, indScenId: number,
                                       gmid: string, fcorg: string, propTypeId: number, engClassId: number, fcstateId: number,
                                       scenId: number): Observable<void> {
    const dto: VehicleScenarioCreateDtoModel = {
      name,
      scenCat,
      scenType,
      assm,
      scenId,
      indScenId,
      keepSharedVol: null,
      propTypeId,
      engClassId,
      fcStateId: fcstateId,
      gmid,
      fcorg,
      programList: null,
    };

    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/from-scenario-tvp`, dto)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   *
   * Sends a post request to the endpoint to create a TVP scenario from portfolio
   * @param name the scenario name
   * @param scenCat the scenario category
   * @param scenType the scenario type
   * @param assm the scenario assumption
   * @param indScenId the id of the related industry scenario
   * @param gmid the gmid of the user
   * @param fcorg the chosen fcorg
   * @param propTypeId the propulsion type id
   * @param engClassId the engineering program class
   * @param fcStateId the forecast state id
   * @param programList the portion of the query that filters by the selected vehicle programs
   */
  createVehicleScenarioFromPortfolioTVP(name: string, scenCat: string, scenType: string, assm: string, indScenId: number,
                                        gmid: string, fcorg: string, propTypeId: number, engClassId: number,
                                        fcStateId: number, programList: string): Observable<void> {
    const dto: VehicleScenarioCreateDtoModel = {
      name,
      scenCat,
      scenType,
      assm,
      scenId: null,
      indScenId,
      keepSharedVol: null,
      propTypeId,
      engClassId,
      fcStateId,
      gmid,
      fcorg,
      programList,
    };
    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/from-portfolio-tvp`, dto)
    .pipe(catchError(myError => this.handleError(myError)));
  }


  /**
   * Update which propulsion scenario is attached to the specified Vehicle Scenario
   * @param propulsionScenarioId the ID of the propulsion scenario to attach
   * @param vehicleScenarioId the ID of the vehicle scenario to update
   */
  updateAttachedPropulsionScenario(propulsionScenarioId: number, vehicleScenarioId: number): Observable<any> {
    return this.http.put<any[]>(`${this.configService.baseApiUrl}vehicle-scenario/attach-propulsion-scenario`, {}, {
      params: new HttpParams().set('propulsionScenarioId', propulsionScenarioId).set('vehicleScenarioId', vehicleScenarioId)
    }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Get the Start year and End Year of a vehicle scenario based off the vehicle scenario details
   * @param vehScenarioId the Vehicle Scenario id
   */
  getYearRange(vehScenarioId: number): Observable<any> {
    const params = new HttpParams()
    .set('vehScenId', vehScenarioId);
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario/year-range`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get the data that is used for the filters for the GM Market Share of Segment Report
   * @param gmid the gmid of the user
   * @param workingVehScenId the working vehicle scenario id
   * @param isGlobal whether you are getting data for the filters from a vehicle scenario or global scenario
   */
  getFiltersForMarketShareSegmentReport(gmid: string, workingVehScenId: number, isGlobal: number): Observable<any> {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('workingVehScenId', workingVehScenId)
    .set('isGlobal', isGlobal);
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario/market-share-sgmnt-rpt/filters`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get the resulting data for the GM Market Share of Segment report
   * @param request the report request object
   */
  getMarketShareSegmentReport(request: MarketShareSegmentReportRequest): Observable<any> {
    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/market-share-sgmnt-rpt`, request)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get all the architectures for a vehicle scenario
   * @param vehScenId the id of the vehicle scenario
   */
  getArchByVehScenId(vehScenId: number): Observable<string[]> {
    const params = new HttpParams()
    .set('vehScenId', vehScenId);
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario/architectures`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get list of TVP vehicle programs by architecture and vehicle scenario id that is in the scenario
   * and in the vehicle portfolio
   * @param vehScenId id of the vehicle scenario
   * @param arch the architecture
   */
  getTVPVehicleProgramsByArchAndVehScenId(vehScenId: number, arch: string): Observable<TvpVehicleProgram[]> {
    const params = new HttpParams()
    .set('vehScenId', vehScenId)
    .set('arch', arch);
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario/tvp-programs`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get data for the filters for vehicle scenario details
   */
  getFiltersForQueryVehicle(gmid: string, workingVehScenId: number) {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('vehScenId', workingVehScenId);
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario/query-filters`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Exports a vehicle forecast template
   * @param requestDTO the DTO housing all the query parameters
   * @return the vehicle forecast template workbook
   */
  createVehicleForecastTemplate(requestDTO: VehicleQueryRequestDTO) {
    const headers = new HttpHeaders();
    return this.http.post(`${this.configService.baseApiUrl}vehicle-scenario/forecast-template`,
      requestDTO, {headers, responseType: 'blob'}).pipe(catchError(error => this.handleErrorBlob(error)));
  }


  /**
   * gets the raw data
   * @param requestDTO the DTO housing all the query parameters
   * @return the vehicle forecast template workbook
   */
  getForecastTemplateData(requestDTO: VehicleQueryRequestDTO) {
    const headers = new HttpHeaders();
    return this.http.post(`${this.configService.baseApiUrl}vehicle-scenario/forecast-template/data`,
      requestDTO, {headers}).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Updates a vehicle forecast with the data provided
   * @param data The modified raw vehicle forecast data
   * @param gmid the user's GMID
   */
  updateVehicleForecast(data: any[], gmid: string) {
    const headers = new HttpHeaders();
    const params = new HttpParams().set('gmid', gmid);
    return this.http.put(`${this.configService.baseApiUrl}vehicle-scenario/forecast-template/update`,
      data, {headers, params}).pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Save a vehicle forecast template
   * @param file
   * @param sheetName
   * @param gmid
   */
  saveVehicleForecastTemplate(file: File, sheetName: string, gmid: string): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-name', sheetName);
    formData.append('gmid', gmid);

    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/forecast-template/save`, formData)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Get the vehicle scenario from the forecast template
   * @param file the file to upload
   * @param sheetName the name of the sheet to use
   * @param gmid the user's GMID
   */
  getScenarioFromForecastTemplate(file: File, sheetName: string, gmid: string): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-name', sheetName);
    formData.append('gmid', gmid);

    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/forecast-template/scenario`, formData)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Runs the global vehicle consolidation report
   *
   * @param scenIds      the ID's of the scenarios for the report
   * @param startYear    The start year for the report
   * @param endYear      The end year for the report
   * @param gmid         The user's GMID
   * @param aggregateTos A string flag to aggregate by TOS or not
   * @param zeroVolumes  A string flag for if records with zero volumes should be displayed
   * @param dropRecidNa  A string flag to drop records with an ID = NA
   * @param includeLca   A string flag to include the LCA calculation
   */
  getConsolidationReport(scenIds: string, startYear: number, endYear: number, gmid: string, aggregateTos: string,
                         zeroVolumes: string, dropRecidNa: string, includeLca: string) {
    const params = new HttpParams()
    .set('scen-ids', scenIds)
    .set('start-year', startYear)
    .set('end-year', endYear)
    .set('gmid', gmid)
    .set('aggregate-tos', aggregateTos)
    .set('zero-volumes', zeroVolumes)
    .set('drop-recid-na', dropRecidNa)
    .set('include-lca', includeLca);
    return this.http.get(`${this.configService.baseApiUrl}vehicle-scenario/consolidation-report`, {
      params,
      responseType: 'blob'
    })
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Method that uses the provided filters to obtain a list of TVP scenario Ids to filter with
   *
   * @param requestBody the request body with all the filter parameters
   */
  getFilteredIDs(requestBody: FilterTvpScenarioRequestModel): Observable<any> {

    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/consolidation-report/tvp-ids`, requestBody)
    .pipe(catchError(myError => this.handleError(myError)));

  }

  /**
   * Get the Global Segment Share Audit report
   * @param vehicleScenIds string of vehicle scenario ids to run the report on
   * @param industryScenIds string of industry scenario ids to run the report on
   * @param startYear the start year of the report
   * @param endYear the end year of the report
   * @param gmid the id of the user
   * @param combinePhevIce whether to combine PHEV and ICE volumes
   */
  getGlobalSgmntShrAuditRpt(vehicleScenIds: string, industryScenIds: string, startYear: number, endYear: number,
                            gmid: string, combinePhevIce: boolean): Observable<any> {
    const params = new HttpParams()
    .set('vehicleScenIds', vehicleScenIds)
    .set('industryScenIds', industryScenIds)
    .set('startYear', startYear)
    .set('endYear', endYear)
    .set('gmid', gmid)
    .set('combinePhevIce', combinePhevIce);
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario/global-sgmnt-shr-audit-rpt`,
      {params: params, headers: new HttpHeaders(), responseType: 'blob' as 'json'})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets the filters for the Vehicle Propulsion Volume Comparison report
   * @param gmid the gmid of the user
   * @param vehScenId the vehicle scenario id
   */
  getFiltersForVehPropVolCompare(vehScenId: number, gmid: string): Observable<any> {
    const params = new HttpParams()
    .set('veh-scen-id', vehScenId)
    .set('gmid', gmid);
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario/veh-prop-vol-compare/filters`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * gets an excel workbook with the raw pivot table data
   * @param dto
   */
  getPivotTableData(dto: PivotTableVehDataRequestDTO): any {
    return this.http.post(`${this.configService.baseApiUrl}vehicle-scenario/pivot-table-data`, dto, {responseType: 'blob'})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets a list of filtered records for a vehicle scenario
   * @param request the request DTO to hold the request parameters
   */
  getFilteredRecords(request: UpdateForecastWithScenarioRequest): Observable<any> {
    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/forecast-update/filtered-records`, request)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets a list of matching records for two vehicle scenarios
   * @param request the request DTO to hold the request parameters
   */
  getMatchingRecords(request: UpdateForecastWithScenarioRequest): Observable<any> {
    return this.http.post<any>(`${this.configService.baseApiUrl}vehicle-scenario/forecast-update/matching-records`, request)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * gets the filters for the update
   * @param gmid        The user's GMID
   * @param vehScenId   the scenario to get the records for
   * @param region      The region to get records for
   */
  getUpdateForecastFilters(gmid: string, vehScenId: number, region: string): Observable<any> {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('veh-scen-id', vehScenId)
    .set('region', region)
    return this.http.get<any>(`${this.configService.baseApiUrl}vehicle-scenario/forecast-update/filters`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Update the forecast with the scenario data
   * @param request the request DTO to hold the request parameters
   */
  updateForecastWithScenario(request: UpdateForecastWithScenarioRequest): any {
    return this.http.post(`${this.configService.baseApiUrl}vehicle-scenario/forecast-update`, request, {responseType: 'blob'})
    .pipe(catchError(myError => this.handleErrorBlob(myError)));
  }

  /**
   * Update the scenario with external data
   * @param file the vehicle forecast template file
   * @param sheetName the name of the sheet that has the vehicle forecast template data
   * @param gmid the GMID of the user
   * @param vehScenId the id of the vehicle scenario to update
   * @param isPreprocess whether to commit the updates or not
   * @param isUpdateDate whether to update the date or not
   * @param isUpdateComment whether to update the comment or not
   */
  updateWithExternalData(file: File, sheetName: string, gmid: string, vehScenId: number, isPreprocess: boolean,
                         isUpdateDate: boolean, isUpdateComment: boolean): any {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-name', sheetName);
    formData.append('gmid', gmid);
    formData.append('veh-scen-id', vehScenId.toString());
    formData.append('is-preprocess', isPreprocess.toString());
    formData.append('is-update-date', isUpdateDate.toString());
    formData.append('is-update-comment', isUpdateComment.toString());
    return this.http.post(`${this.configService.baseApiUrl}vehicle-scenario/update-with-external-data`, formData, {responseType: 'blob'})
    .pipe(catchError(myError => this.handleErrorBlob(myError)));
  }

  /**
   * Gets the Vehicle Detail Records
   * @param gmid The gmid of the user
   * @param workingScenId The working scenario id
   * @param fcorg the forecast organization for the scenario
   */
  getVehicleDetailRecordsForDelete(gmid: string, workingScenId: number, fcorg: string) {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('scen-id', workingScenId)
    .set('fcorg', fcorg);

    return this.http.get(`${this.configService.baseApiUrl}vehicle-scenario/detail-records`, {params, responseType: 'blob'})
    .pipe(catchError(error => this.handleErrorBlob(error)));
  }

  /**
   * Validates the vehicle detail records for deletion
   * @param file The file to validate
   * @param sheetNames The names of the sheets to validate
   * @param vehScenId The vehicle scenario ID
   * @param fcorg The forecast organization
   */
  validateVehDetailRecordsForDelete(file: File, sheetNames: string[], vehScenId: number, fcorg: string) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-name', sheetNames.join(','));
    formData.append('veh-scen-id', vehScenId.toString());
    formData.append('fcorg', fcorg);
    return this.http.post(`${this.configService.baseApiUrl}vehicle-scenario/detail-records/validate`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Deletes vehicle records that are marked in the file
   * @param file The file to delete records from
   * @param sheetNames The names of the sheets to delete records from
   * @param gmid The user's GMID
   */
  deleteVehicleRecords(file: File, sheetNames: string[], gmid: string) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-name', sheetNames.join(','));
    formData.append('gmid', gmid);
    return this.http.post(`${this.configService.baseApiUrl}vehicle-scenario/detail-records/delete`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Saves a global vehicle forecast template
   * @param file The file to import
   * @param sheetName The name of the sheet to import
   * @param gmid The GMID of the user
   */
  saveGlobalVehicleForecast(file: File, sheetName: string, gmid: string) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheet-name', sheetName);
    formData.append('gmid', gmid);
    return this.http.post(`${this.configService.baseApiUrl}vehicle-scenario/forecast-template/global/save`, formData)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   * @return void
   */
  handleError(errorResponse: HttpErrorResponse): any {
    let errorMessage = 'An unexpected error occurred.';
    switch (errorResponse.status) {
      case HttpStatusCode.UnprocessableEntity:
      case HttpStatusCode.BadRequest: {
        if (errorResponse.error && errorResponse.error.message) {
          errorMessage = errorResponse.error.message;
        }
        break;
      }
    }
    return throwError(errorMessage);
  }

  /**
   * Handles the errors produced by the backend when the response type is a blob
   * @param err The error produced by the API to be handled by the frontend
   */
  public handleErrorBlob(err: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();
    const obs = new Observable((observer: any) => {
      reader.onloadend = () => {
        const result = JSON.parse(reader.result as string);
        observer.error(result.message);
        observer.complete();
      };
    });
    reader.readAsText(err.error);
    return obs;
  }
}
